import { Typography, Box } from '@mui/material';
import { Tooltip } from '@components/tooltip';
import { useGetScreenSize } from '@utils/useGetScreenSize';
import { useStore } from '@store/GlobalStore';
import { DatePicker } from '@components/datePicker';
import { fakeLocalMidnightFromTZMidnight } from '@utils/date';
import { InfoCircle } from '@assets/svgs';
import { AvailableBrokerageOfferingsItem } from '@models/productOffering';
import { useNewQuoteStore } from '@store/NewQuote';
import { getEarliestEffectiveOn, getLatestEffectiveThrough } from '@utils/brokerages';
import { addDays, addYears, isValid } from 'date-fns';
import { IconButton } from '@components/iconButton';
import { shallow } from 'zustand/shallow';
import { useProductOfferings } from '@services/productOffering';
import { useProductOptions } from '@services/productOption';
import { uniqBy } from 'lodash';
import { QuoteSetupPresetGetResponse } from '@lio/api-types';
import { useMemo } from 'react';
type TPolicyDateSelection = {
  handleSelectedDateChange: (
    effectiveDate: Date | null,
    expirationDate: Date | null,
    context: { validationError: string | null },
  ) => void;
  quotePreset?: QuoteSetupPresetGetResponse;
};

export const EventDateRangeSelection = ({ handleSelectedDateChange, quotePreset }: TPolicyDateSelection) => {
  const { scope } = useNewQuoteStore();
  const { brokerageAgency, productOffering, insuranceProductId, productOption, expirationDate, effectiveDate } = scope;
  const [userAuth] = useStore((state) => [state.userAuth], shallow);
  const { isInternalUser } = userAuth;

  const hasQuotePreset = quotePreset;
  const { productOfferings } = useProductOfferings({ brokerageId: brokerageAgency?.value });
  const { productOptions } = useProductOptions(insuranceProductId);
  const uniqueOfferings = useMemo(
    () =>
      productOfferings?.items.length
        ? uniqBy(productOfferings.items, (brokerageOffering) => brokerageOffering.id).map((p) => ({
            ...p,
            brokerageOfferingAvailability: {
              jurisdictions: p.brokerageOfferingAvailability.jurisdictions.map((j) => ({
                ...j,
                effectiveOn: new Date(j.effectiveOn),
                effectiveThrough: new Date(j.effectiveThrough),
              })),
            },
          }))
        : [],
    [productOfferings],
  );
  const { isMobileScreen } = useGetScreenSize();
  const datePickerParams = useMemo(() => {
    const productId = productOffering?.value;
    const effectiveProduct = uniqueOfferings?.find((item: AvailableBrokerageOfferingsItem) => item.id === productId);

    const effectiveDate = effectiveProduct
      ? getEarliestEffectiveOn([...effectiveProduct.brokerageOfferingAvailability.jurisdictions])
      : new Date();
    const effectiveThroughData = effectiveProduct
      ? getLatestEffectiveThrough([...effectiveProduct.brokerageOfferingAvailability.jurisdictions])
      : null;
    // note, when/if this becomes combined with similar components only special events should add 1 year, the rest should do 120 days for all this logic
    const effectiveThrough = effectiveThroughData || addYears(new Date(), 1);
    return {
      minEffectiveDate: isInternalUser
        ? effectiveDate
        : new Date(Math.max(new Date().valueOf(), effectiveDate.valueOf())),
      maxEffectiveDate: new Date(Math.min(addYears(new Date(), 1).valueOf(), effectiveThrough.valueOf())),
    };
  }, [productOffering?.value, uniqueOfferings, isInternalUser]);

  const isDateRangeRestricted = useMemo(() => {
    return (
      new Date(datePickerParams.maxEffectiveDate) < addYears(new Date(), 1) ||
      new Date(datePickerParams.minEffectiveDate) > new Date()
    );
  }, [datePickerParams.maxEffectiveDate, datePickerParams.minEffectiveDate]);

  const isEffectiveDateDisabled = hasQuotePreset
    ? false
    : productOffering
      ? !!((uniqueOfferings.length && !productOffering) || (productOptions?.items.length && !productOption))
      : true;

  const minDate =
    effectiveDate && isValid(effectiveDate)
      ? fakeLocalMidnightFromTZMidnight(effectiveDate)
      : fakeLocalMidnightFromTZMidnight(datePickerParams.minEffectiveDate);
  const maxDate = isValid(minDate) ? addYears(minDate, 1) : null;

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 2 }}>
        <Typography variant='h4'>What are the dates for your event?</Typography>
        <Tooltip
          sx={{ marginRight: '-36px' }}
          placement='left-start'
          title='Event start and end dates should include any coverage that is needed for set up and take down'
        >
          <IconButton variant='circularDark'>
            <InfoCircle />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: isMobileScreen ? 'column' : 'row', gap: 2 }}>
        <DatePicker
          textFieldProps={{
            inputLabel: 'Event Start Date',
          }}
          value={effectiveDate !== null ? new Date(effectiveDate) : effectiveDate}
          data-testid='Start-Date'
          disabled={isEffectiveDateDisabled}
          onChange={(value, context) => handleSelectedDateChange(value, expirationDate, context)}
          minDate={fakeLocalMidnightFromTZMidnight(datePickerParams.minEffectiveDate)}
          maxDate={
            isValid(expirationDate)
              ? fakeLocalMidnightFromTZMidnight(expirationDate ? expirationDate : datePickerParams.maxEffectiveDate)
              : datePickerParams.maxEffectiveDate
          }
        />
        <DatePicker
          value={expirationDate !== null ? new Date(expirationDate) : expirationDate}
          data-testid='End-Date'
          disabled={isEffectiveDateDisabled || !effectiveDate}
          onChange={(value, context) => handleSelectedDateChange(effectiveDate, value, context)}
          minDate={minDate ? addDays(minDate, 1) : undefined}
          maxDate={maxDate ?? undefined}
          textFieldProps={{
            inputLabel: 'Event End Date',
          }}
        />
        {!isEffectiveDateDisabled && isDateRangeRestricted && (
          <Tooltip
            sx={{ marginRight: '-36px' }}
            placement='left-start'
            title='Date range is restricted due to product availability'
          >
            <IconButton variant='circularDark'>
              <InfoCircle />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </>
  );
};
